import { createParentRpcBridge } from "@shared/lib/rpc/parent-bridge";
import type { RpcClient } from "@shared/lib/rpc/types";
import { consoleDev } from "@shared/lib/console-dev";
import type { WordChatApi } from "@shared/lib/rpc/apis/word-chat-api";
import { SyncEngine } from "../sync-engine/SyncEngine";
import { GetSurfaceContextOperation } from "./operations/GetSurfaceContextOperation";
import { WordAddInApiHandler } from "./WordAddInApiHandler";

export interface WordRpcBridgeConfig {
  iframe: HTMLIFrameElement;
  workspaceId: string;
  syncEngine: SyncEngine;
  onSwitchMode?: (mode: "chat" | "automation") => void;
  automationModeEnabled?: boolean;
}

export interface WordRpcBridge {
  /** Client to call the chat panel's API */
  chatRpc: RpcClient<WordChatApi>;
  /** Push current surface context to the chat panel */
  pushSurfaceContext: () => Promise<void>;
  /** Clean up the RPC bridge */
  cleanup: () => void;
}

/**
 * Initialize the RPC bridge between Word add-in and chat iframe.
 *
 * Uses the shared parent bridge infrastructure with Word-specific
 * API handler and helper methods.
 *
 * @example
 * ```typescript
 * const bridge = await createWordRpcBridge({
 *   iframe,
 *   workspaceId: "123",
 *   syncEngine,
 *   onSwitchMode: (mode) => console.log("Switch to:", mode),
 * });
 *
 * // Push surface context on selection change
 * await bridge.pushSurfaceContext();
 *
 * // Clean up on unmount
 * bridge.cleanup();
 * ```
 */
export async function createWordRpcBridge(config: WordRpcBridgeConfig): Promise<WordRpcBridge> {
  const { iframe, workspaceId, syncEngine, onSwitchMode, automationModeEnabled } = config;

  // Create the base bridge using shared infrastructure
  const bridge = await createParentRpcBridge<WordAddInApiHandler, WordChatApi>({
    iframe,
    app: "word",
    workspaceId,
    features: { automationModeEnabled },
    createApiHandler: () =>
      new WordAddInApiHandler({
        syncEngine,
        onSwitchMode,
      }),
  });

  // Helper to push current surface context
  async function pushSurfaceContext(): Promise<void> {
    try {
      const context = await syncEngine.perform(new GetSurfaceContextOperation());
      await bridge.rpc.onSurfaceContextChanged({ context });
    } catch (err) {
      consoleDev.error("[RPC:WordAddIn] Failed to push surface context:", err);
    }
  }

  return {
    get chatRpc() {
      return bridge.rpc;
    },
    pushSurfaceContext,
    cleanup: bridge.cleanup,
  };
}
